// @ts-nocheck
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import FinnMap from '@finn-no/finn-map-react';
import { FullscreenControl, type GeoJSONSource, LngLatBounds, type Map as MapboxMap, NavigationControl, Popup } from 'mapbox-gl';
import useOnClickOutside from 'react-cool-onclickoutside';

import { UpdateBoundsButton } from './UpdateBoundsButton';
import type { LngLatArr, MapBounds, MapProps } from './interfaces';
import { getAllPoiTypes, getMapPopup, loadAllPoiImages, poiSource } from './pois/poiConfig';
import { PoiPopup } from './pois/popups/PoiPopup';
import {
    convertToGeoJson,
    createGeoJsonSource,
    type MapImage,
    type MapLayer,
    type MapSource,
    PRIMARY_CLUSTER_COUNT_HIGH_ZOOM_LAYER,
    PRIMARY_CLUSTER_COUNT_LOW_ZOOM_LAYER,
    PRIMARY_CLUSTER_HIGH_ZOOM_LAYER,
    PRIMARY_CLUSTER_LOW_ZOOM_LAYER,
    PRIMARY_CLUSTER_SHADOW_HIGH_ZOOM_LAYER,
    PRIMARY_CLUSTER_SHADOW_LOW_ZOOM_LAYER,
    PRIMARY_HOVER_PIN_ICON,
    PRIMARY_PIN_ICON,
    PRIMARY_POINT_HIGH_ZOOM_LAYER,
    PRIMARY_POINT_LOW_ZOOM_LAYER,
    PRIMARY_PRICE_HOVER_PIN_ICON,
    PRIMARY_PRICE_PIN_ICON,
    PRIMARY_PRICE_POINT_HIGH_ZOOM_LAYER,
    PRIMARY_PRICE_POINT_LOW_ZOOM_LAYER,
    SELECTED_PIN_ICON,
    SELECTED_POINT_LAYER,
    SELECTED_PRICE_PIN_ICON,
    SELECTED_PRICE_POINT_LAYER,
    SERVER_SIDE_CLUSTER_COUNT_LAYER,
    SERVER_SIDE_CLUSTER_LAYER,
    SERVER_SIDE_CLUSTER_SHADOW_LAYER,
} from './mapConfig';
import type { GeoJsonFeatureCollection } from './geoJson';
import { LoadingIndicator } from './LoadingIndicator';

import '@finn-no/finn-map/dist/style.css';
import './map.css';
import type { PoiInfoList } from './pois/interfaces';

interface SourceObject {
    source?: string;
    sourceLayer?: string;
}

const createSourceObject = (layer: MapLayer): SourceObject => {
    const source = (layer?.options?.source && String(layer?.options?.source)) || '';
    const sourceLayer = (layer?.options['source-layer'] && String(layer?.options['source-layer'])) || '';
    return {
        source,
        sourceLayer,
    };
};

const updateSource = (map: MapboxMap, id: string, data: GeoJsonFeatureCollection): void => {
    (map.getSource(id) as GeoJSONSource).setData(data);
};

const addSource = (map: MapboxMap, source: MapSource, condition = true): void => {
    if (!condition) {
        return;
    }

    if (map.getSource(source.id)) {
        updateSource(map, source.id, source.options.data);
        return;
    }

    map.addSource(source.id, source.options);
};

const addLayer = (map: MapboxMap, config: MapLayer, condition = true): void => {
    if (!condition) {
        return;
    }

    if (map.getLayer(config.id)) {
        map.removeLayer(config.id);
    }

    map.addLayer({ id: config.id, ...config.options });
};

const addPin = (map: MapboxMap, config: MapImage) => {
    map.loadImage(config.url, (e, image) => {
        if (e) {
            console.error(`Failed loading image (${config.url})`, e);
            return;
        }

        if (map.hasImage(config.id)) {
            map.removeImage(config.id);
        }

        map.addImage(config.id, image, { ...config.options });
    });
};

const popupOnHover = (map: MapboxMap, layerId: string, offset: [number, number] = [0, 0]) => {
    let popup: Popup;
    map.on('mouseenter', layerId, (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
        const { name, link } = e.features[0].properties;
        if (link) {
            const html = `<a href="${link}">${name}</a>`;
            popup = new Popup({ offset, closeButton: false }).setLngLat(coordinates).setHTML(html).addTo(map);
        } else {
            popup = new Popup({ offset, closeButton: false }).setLngLat(coordinates).setText(name).addTo(map);
        }
    });
    map.on('mouseleave', layerId, () => {
        popup.remove();
    });
};

const fetchExternalPopupOnClick = (map: MapboxMap, layerId: string) => {
    map.on('click', layerId, (e) => {
        const features = map.queryRenderedFeatures(e.point, {
            layers: [layerId],
        });
        const feature = features[0];

        if (!feature?.properties?.ids) {
            return;
        }

        getMapPopup(feature.properties.ids, (data: PoiInfoList) => {
            const div: HTMLDivElement = window.document.createElement('div');

            ReactDOM.render(<PoiPopup {...data} />, div);

            new Popup({
                closeButton: true,
                offset: [0, -6],
            })
                .setLngLat(feature.geometry.coordinates)
                .setDOMContent(div)
                .setMaxWidth('550px')
                .addTo(map);
        });
    });
};

const addPinClickEvent = (map: MapboxMap, layerId: string, onPinCLick: (id: string) => void) => {
    map.on('click', layerId, (e) => {
        onPinCLick(e.features[0].properties.id);
    });
};

const zoomAndCenterOnClick = (map: MapboxMap, layerId: string, sourceId: string, condition = true) => {
    if (!condition) {
        return;
    }

    map.on('click', layerId, (e) => {
        const features = map.queryRenderedFeatures(e.point, {
            layers: [layerId],
        });
        const clusterId = features[0].properties.cluster_id;

        (map.getSource(sourceId) as GeoJSONSource).getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;
            map.easeTo({
                center: features[0].geometry.coordinates,
                zoom: zoom + 1,
            });
        });
    });
};

const zoomAndCenterOnClusterClick = (map: MapboxMap, layerId: string) => {
    map.on('click', layerId, (e) => {
        map.easeTo({
            center: e.features[0].geometry.coordinates,
            zoom: map.getZoom() + 1,
        });
    });
    // Change the cursor to a pointer when the it enters a feature in the 'circle' layer.
    map.on('mouseenter', layerId, () => {
        map.getCanvas().style.cursor = 'pointer';
    });

    // Change it back to a pointer when it leaves.
    map.on('mouseleave', layerId, () => {
        map.getCanvas().style.cursor = '';
    });
};

const mouseHover = (map: MapboxMap, layerId: string, source: SourceObject, setHoverId: (id: string) => void, condition = true) => {
    if (!condition) {
        return;
    }

    let featureId: string | undefined;

    map.on('mouseenter', layerId, (e) => {
        map.getCanvas().style.cursor = 'pointer';

        const { cluster, cluster_id: clusterId, id, ids } = e.features[0].properties;
        featureId = cluster ? clusterId : (id ?? ids);

        if (featureId) {
            map.setFeatureState({ ...source, id: featureId }, { hover: true });
        }

        setHoverId(id);
    });

    map.on('mouseleave', layerId, () => {
        map.getCanvas().style.cursor = '';

        if (featureId) {
            map.setFeatureState({ ...source, id: featureId }, { hover: false });
            featureId = undefined;
        }

        setHoverId(undefined);
    });
};

const flyTo = (map: MapboxMap, coordinates: LngLatArr, minimumZoom: number, animate: boolean): void => {
    const zoom = Math.max(map.getZoom(), minimumZoom);
    map.flyTo({
        center: coordinates,
        animate,
        zoom,
    });
};

const fitBounds = (map: MapboxMap, coordinatesSet: LngLatArr[], maxZoom: number): void => {
    const bounds = new LngLatBounds();
    coordinatesSet.forEach((coordinates) => {
        bounds.extend(coordinates);
    });
    map.fitBounds(bounds, {
        animate: false,
        padding: 10,
        maxZoom,
    });
};

const keepTrackOfBounds = (
    map: MapboxMap,
    setCurrentBounds: (bounds: MapBounds) => void,
    setBoundsChanged: (changed: boolean) => void,
    onBoundsChanged?: (bounds: MapBounds) => void,
): void => {
    map.on('moveend', () => {
        const bounds: LngLatBounds = map.getBounds();
        const mapBounds: MapBounds = {
            sw: bounds.getSouthWest().toArray(),
            ne: bounds.getNorthEast().toArray(),
        };
        setCurrentBounds(mapBounds);
        if (onBoundsChanged) {
            onBoundsChanged(mapBounds);
        }
        setBoundsChanged(true);
    });
};

const coordinatesAreEqual = (coordinate1?: LngLatArr, coordinate2?: LngLatArr): boolean =>
    (coordinate1 === coordinate2) === undefined ||
    (coordinate1 && coordinate2 && coordinate1[0] === coordinate2[0] && coordinate1[1] === coordinate2[1]);

const ClientSideMap = ({
    id,
    initialZoom = 12,
    initialBounds,
    selectedPois = [],
    primaryPois = [],
    poiTypes = [],
    serverSideClusters = [],
    center,
    hasClustering = false,
    onPinClick,
    onUpdateBoundsClick,
    canUpdateBoundsOnClick,
    onUpdateBounds,
    loading = false,
    disableScrollZoomUnlessFocused = false,
    fullscreenControl = false,
    className = '',
    children,
}: MapProps) => {
    // biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
    const mapRef = useRef<any>();
    const [map, setMap] = useState<MapboxMap>();
    const [allowScrollZoom, setAllowScrollZoom] = useState(false);
    const [boundsChanged, setBoundsChanged] = useState(false);
    const [currentBounds, setCurrentBounds] = useState<MapBounds | undefined>(initialBounds);
    const [currentCenter, setCurrentCenter] = useState<LngLatArr | undefined>();
    const [hoverId, setHoverId] = useState<string | undefined>();
    const [poiLayerLoaded, setPoiLayerLoaded] = useState(false);
    const [poiImages, setPoiImages] = useState<MapImage[] | undefined>();

    const mapContainerRef = useOnClickOutside(
        (): void => {
            map.scrollZoom.disable();
            setAllowScrollZoom(false);
        },
        {
            disabled: !disableScrollZoomUnlessFocused || !allowScrollZoom,
        },
    );

    const onTouch = (): void => {
        if (!map || !disableScrollZoomUnlessFocused) {
            return;
        }

        map.scrollZoom.enable();
        setAllowScrollZoom(true);
    };

    useEffect(() => {
        const mapbox: MapboxMap = mapRef.current.mapbox;
        mapbox.once('load', () => {
            if (initialBounds) {
                mapbox.fitBounds([initialBounds.sw, initialBounds.ne], {
                    animate: false,
                });
                setCurrentCenter([initialBounds.sw[0] - initialBounds.ne[0], initialBounds.sw[1] - initialBounds.ne[1]]);
            }

            if (disableScrollZoomUnlessFocused) {
                mapbox.scrollZoom.disable();
            }

            mapbox.addControl(new NavigationControl({ showCompass: true }));
            if (fullscreenControl) {
                mapbox.addControl(new FullscreenControl({ container: document.querySelector(`#${id}`) }));
            }

            addSource(mapbox, createGeoJsonSource(SELECTED_POINT_LAYER.options.source, []));
            addSource(mapbox, createGeoJsonSource(PRIMARY_POINT_LOW_ZOOM_LAYER.options.source, primaryPois, hasClustering));
            addSource(mapbox, createGeoJsonSource(PRIMARY_POINT_HIGH_ZOOM_LAYER.options.source, primaryPois, hasClustering, 25));
            addSource(mapbox, createGeoJsonSource(SERVER_SIDE_CLUSTER_LAYER.options.source, []));

            addPin(mapbox, SELECTED_PIN_ICON);
            addPin(mapbox, SELECTED_PRICE_PIN_ICON);
            addPin(mapbox, PRIMARY_PIN_ICON);
            addPin(mapbox, PRIMARY_HOVER_PIN_ICON);
            addPin(mapbox, PRIMARY_PRICE_PIN_ICON);
            addPin(mapbox, PRIMARY_PRICE_HOVER_PIN_ICON);

            addLayer(mapbox, SERVER_SIDE_CLUSTER_SHADOW_LAYER);
            addLayer(mapbox, SERVER_SIDE_CLUSTER_LAYER);
            addLayer(mapbox, SERVER_SIDE_CLUSTER_COUNT_LAYER);
            addLayer(mapbox, PRIMARY_CLUSTER_SHADOW_LOW_ZOOM_LAYER, hasClustering);
            addLayer(mapbox, PRIMARY_CLUSTER_LOW_ZOOM_LAYER, hasClustering);
            addLayer(mapbox, PRIMARY_CLUSTER_COUNT_LOW_ZOOM_LAYER, hasClustering);
            addLayer(mapbox, PRIMARY_POINT_LOW_ZOOM_LAYER);
            addLayer(mapbox, PRIMARY_PRICE_POINT_LOW_ZOOM_LAYER);
            addLayer(mapbox, PRIMARY_CLUSTER_SHADOW_HIGH_ZOOM_LAYER, hasClustering);
            addLayer(mapbox, PRIMARY_CLUSTER_HIGH_ZOOM_LAYER, hasClustering);
            addLayer(mapbox, PRIMARY_CLUSTER_COUNT_HIGH_ZOOM_LAYER, hasClustering);
            addLayer(mapbox, PRIMARY_POINT_HIGH_ZOOM_LAYER);
            addLayer(mapbox, PRIMARY_PRICE_POINT_HIGH_ZOOM_LAYER);
            addLayer(mapbox, SELECTED_POINT_LAYER);
            addLayer(mapbox, SELECTED_PRICE_POINT_LAYER);

            mouseHover(mapbox, SERVER_SIDE_CLUSTER_LAYER.id, createSourceObject(SERVER_SIDE_CLUSTER_LAYER), setHoverId, !hasClustering);
            mouseHover(
                mapbox,
                PRIMARY_CLUSTER_LOW_ZOOM_LAYER.id,
                createSourceObject(PRIMARY_POINT_LOW_ZOOM_LAYER),
                setHoverId,
                hasClustering,
            );
            mouseHover(mapbox, PRIMARY_POINT_LOW_ZOOM_LAYER.id, createSourceObject(PRIMARY_POINT_LOW_ZOOM_LAYER), setHoverId);
            mouseHover(mapbox, PRIMARY_PRICE_POINT_LOW_ZOOM_LAYER.id, createSourceObject(PRIMARY_PRICE_POINT_LOW_ZOOM_LAYER), setHoverId);
            mouseHover(
                mapbox,
                PRIMARY_CLUSTER_HIGH_ZOOM_LAYER.id,
                createSourceObject(PRIMARY_POINT_HIGH_ZOOM_LAYER),
                setHoverId,
                hasClustering,
            );
            mouseHover(mapbox, PRIMARY_POINT_HIGH_ZOOM_LAYER.id, createSourceObject(PRIMARY_POINT_HIGH_ZOOM_LAYER), setHoverId);
            mouseHover(mapbox, PRIMARY_PRICE_POINT_HIGH_ZOOM_LAYER.id, createSourceObject(PRIMARY_PRICE_POINT_HIGH_ZOOM_LAYER), setHoverId);
            mouseHover(mapbox, SELECTED_POINT_LAYER.id, createSourceObject(SELECTED_POINT_LAYER), setHoverId);
            mouseHover(mapbox, SELECTED_PRICE_POINT_LAYER.id, createSourceObject(SELECTED_PRICE_POINT_LAYER), setHoverId);

            popupOnHover(mapbox, SELECTED_POINT_LAYER.id, SELECTED_PIN_ICON.offset);
            popupOnHover(mapbox, SELECTED_PRICE_POINT_LAYER.id, SELECTED_PRICE_PIN_ICON.offset);
            popupOnHover(mapbox, PRIMARY_POINT_LOW_ZOOM_LAYER.id, PRIMARY_PIN_ICON.offset);
            popupOnHover(mapbox, PRIMARY_PRICE_POINT_LOW_ZOOM_LAYER.id, PRIMARY_PRICE_PIN_ICON.offset);
            popupOnHover(mapbox, PRIMARY_POINT_HIGH_ZOOM_LAYER.id, PRIMARY_PIN_ICON.offset);
            popupOnHover(mapbox, PRIMARY_PRICE_POINT_HIGH_ZOOM_LAYER.id, PRIMARY_PRICE_PIN_ICON.offset);

            zoomAndCenterOnClick(mapbox, PRIMARY_CLUSTER_LOW_ZOOM_LAYER.id, PRIMARY_CLUSTER_LOW_ZOOM_LAYER.options.source, hasClustering);
            zoomAndCenterOnClick(mapbox, PRIMARY_CLUSTER_HIGH_ZOOM_LAYER.id, PRIMARY_CLUSTER_HIGH_ZOOM_LAYER.options.source, hasClustering);
            zoomAndCenterOnClusterClick(mapbox, SERVER_SIDE_CLUSTER_LAYER.id);
            if (onPinClick) {
                addPinClickEvent(mapbox, PRIMARY_POINT_LOW_ZOOM_LAYER.id, onPinClick);
                addPinClickEvent(mapbox, PRIMARY_PRICE_POINT_LOW_ZOOM_LAYER.id, onPinClick);
                addPinClickEvent(mapbox, PRIMARY_POINT_HIGH_ZOOM_LAYER.id, onPinClick);
                addPinClickEvent(mapbox, PRIMARY_PRICE_POINT_HIGH_ZOOM_LAYER.id, onPinClick);
                addPinClickEvent(mapbox, SELECTED_POINT_LAYER.id, onPinClick);
                addPinClickEvent(mapbox, SELECTED_PRICE_POINT_LAYER.id, onPinClick);
            }
            keepTrackOfBounds(mapbox, setCurrentBounds, setBoundsChanged, onUpdateBounds);
            setMap(mapbox);
        });
        if (poiTypes.length > 0) {
            getAllPoiTypes(poiTypes, (pois: MapImage[]) => setPoiImages(pois));
        }
    }, []);

    useEffect(() => {
        if (!map || !center || coordinatesAreEqual(currentCenter, center)) {
            return;
        }

        flyTo(map, center, initialZoom, currentCenter !== undefined);
        setCurrentCenter(center);

        if (!currentCenter) {
            setBoundsChanged(false);
        }
    }, [map, center]);

    useEffect(() => {
        if (!map || !selectedPois) {
            return;
        }

        updateSource(map, SELECTED_POINT_LAYER.options.source, convertToGeoJson(selectedPois));

        if (!center && !currentCenter && selectedPois.length) {
            flyTo(map, selectedPois[0].coordinates, initialZoom, false);
            setCurrentCenter(selectedPois[0].coordinates);
            setBoundsChanged(false);
        }
    }, [map, selectedPois]);

    useEffect(() => {
        if (!map || !primaryPois) {
            return;
        }

        updateSource(
            map,
            PRIMARY_POINT_LOW_ZOOM_LAYER.options.source,
            convertToGeoJson(primaryPois, hoverId, PRIMARY_HOVER_PIN_ICON.id, PRIMARY_PRICE_HOVER_PIN_ICON.id),
        );
        updateSource(
            map,
            PRIMARY_POINT_HIGH_ZOOM_LAYER.options.source,
            convertToGeoJson(primaryPois, hoverId, PRIMARY_HOVER_PIN_ICON.id, PRIMARY_PRICE_HOVER_PIN_ICON.id),
        );

        if (!currentCenter && !initialBounds && !center && !selectedPois.length && primaryPois.length) {
            fitBounds(
                map,
                primaryPois.map((poi) => poi.coordinates),
                initialZoom,
            );
            setBoundsChanged(false);
            setCurrentCenter(primaryPois[0].coordinates);
        }
    }, [map, primaryPois, hoverId]);

    useEffect(() => {
        if (!map || !serverSideClusters) {
            return;
        }

        updateSource(map, SERVER_SIDE_CLUSTER_LAYER.options.source, convertToGeoJson(serverSideClusters));
    }, [map, serverSideClusters]);

    useEffect(() => {
        if (!map || !poiImages || !poiImages.length || poiLayerLoaded) {
            return;
        }

        setPoiLayerLoaded(true);
        loadAllPoiImages(map, poiImages, (poiLayer: MapLayer) => {
            addSource(map, poiSource(poiTypes));
            addLayer(map, poiLayer);
            mouseHover(map, poiLayer.id, createSourceObject(poiLayer), setHoverId);
            fetchExternalPopupOnClick(map, poiLayer.id);
        });
    }, [map, poiImages]);

    return (
        <div className={`map-container ${className}`} ref={mapContainerRef} onClick={onTouch} onFocus={onTouch}>
            <FinnMap id={id} key="dynamic-map" attributionPosition="bottom-left" ref={mapRef}>
                <div className="mapboxgl-ctrl-top-center">
                    <LoadingIndicator loading={loading} />
                    {currentBounds && (
                        <UpdateBoundsButton
                            bounds={currentBounds}
                            boundsChanged={boundsChanged}
                            loading={loading}
                            onUpdateBoundsClick={onUpdateBoundsClick}
                            canUpdateBoundsOnClick={canUpdateBoundsOnClick}
                            setBoundsChanged={setBoundsChanged}
                        />
                    )}
                </div>
                {children}
            </FinnMap>
        </div>
    );
};

export default ClientSideMap;
